import React, { useState, useContext } from "react";
import { Box, Grid, Link, LinkProps, Stack, TableCell, Tooltip, TooltipProps, tooltipClasses } from "@mui/material";
import { COLOR_MAP } from "../../../../constants/ColorNames";
import styled from "@emotion/styled";
import Tag from "../../../../common/components/Tag";
import { buildQueryString, extractDomain, listDateFormatter } from "../../../../utils/helpers";
import { TABLE_CONSTANTS } from "../../constants";
import { BLAST_ICON } from "../../../../constants/icons/BlastIcon";
import WhatsAppIcon from "../../../../constants/icons/WhatsAppIcon";
import  EmailIcon  from "../../../../constants/icons/EmailIcon";

type CampaignNamePropsType = {
  campaignName: string;
  campaignDate: string;
  status: string;
  showHighlightedColor: boolean,
  campaignType: string,
  pausedBy?: string,
  marketyReason?: string,
  isBulkCampaign: boolean,
  triggeredFromPage: string,
  initiationId: string | number,
  timeZone: string | null |number,
  campaignId: string |number,
  searchId: string | null |number,
  isWhatsAppCampaign: boolean,
  reason?: string,
  resumeAt?: string
  campaignStageLoading: boolean;
}

const Text = styled.span`
  font-size: 1.2rem;
  font-size: 1.2rem;
  line-height: 1.6rem;
  font-weight: 400;
  color: ${COLOR_MAP.GREY_2};
  margin-left: 0.2rem !important;
`;

const DisabledText = styled(Text)`
  color: ${COLOR_MAP.GREY_1};
`;

const Title = (fontColor: string, status: string) => styled.p`
  font-size: 1.4rem;
  line-height: 2rem;
  font-weight: 500;
  margin: 0;
  color: ${fontColor};
  max-width: 116px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    color: ${status === 'DRAFT' ? COLOR_MAP.DARK_PURPLE : 'initial'}
  }
`;

export const CustomStyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  // [`& .${tooltipClasses.arrow}`]: {
  //  fontSize: '1rem'
  // },
  [`& .${tooltipClasses.tooltip}`]: {
       fontSize: '1.2rem',
       marginBottom: '3px !important',
  },
}));

const getStatusText = (status: string) => {
  let text = ''
  text = status.split('_').map((item) => item[0]?.toUpperCase() + item.slice(1).toLowerCase()).join(' ');
  return text;
}

export const ToolTipContainer = styled.div`
  padding: 0.4rem 0.6rem;
  color: ${COLOR_MAP.WHITE};
  background-color: ${COLOR_MAP.LIGHT_GREY};
  border-radius: 0.4rem;
`

const getRemainingHrs = (resumeAt: string) => {
  const now = new Date();
  const resumeDate = new Date(resumeAt);
  const diffInMs = resumeDate.getTime() - now.getTime();
  const diffInHrs = Math.ceil(diffInMs / (1000 * 60 * 60));

  
  if(diffInHrs > 24){
    const days = Math.floor(diffInHrs/24);
    return `${days} ${days > 1 ? "days":"day"}`
  }
  return `${diffInHrs} ${diffInHrs > 1 ? "hrs" : "hr"}`;
}

const ToolTipContent = (props: {campaignName: string}) => {
  const {campaignName } = props;
  return (
    <ToolTipContainer>
      {campaignName}
    </ToolTipContainer>
  )
}
interface StyledLinkProps extends LinkProps {
  status: string;
}
const StyledLink = styled(Link)(({ status }: StyledLinkProps) => ({
  textDecoration: 'none',

  // cursor: status === 'DRAFT' ? 'pointer' : 'default',
  // pointerEvents: status === 'DRAFT' ? 'auto' : 'none',
  '&:hover': {
    color: status === 'DRAFT' ? COLOR_MAP.DARK_PURPLE : 'inherit',
    textDecorationLine : status === 'DRAFT' ? 'underline' : 'none'
  }
}));

const CampaignName = (props: CampaignNamePropsType) => {
  const { campaignName, campaignDate, status,resumeAt, reason, showHighlightedColor, campaignType,campaignStageLoading,  pausedBy, marketyReason, isBulkCampaign, triggeredFromPage, initiationId, timeZone, campaignId, searchId, isWhatsAppCampaign } = props;
  const date = listDateFormatter(campaignDate);
  const CustomTitle = Title(showHighlightedColor ? COLOR_MAP.DARK_PURPLE : COLOR_MAP.GREY_2, status)
  const campaignStatus = status === 'DRAFT' ? 'Drafted' : 'Created';
  let statusText = status ? getStatusText(status) : ''
  statusText = statusText.replaceAll('_','');
  const isBlastCampaign = campaignType === 'BLAST';

  const redirectToCampaignCreate = () => {
    const params= {
      isBulkCampaign,
      triggeredFromPage,
      initiationId,
      timeZone,
      campaignId,
      searchId
    }

    if(isWhatsAppCampaign){
      return(`${extractDomain()}/edge/recruiter-outreach-email/campaign/creation/whatsApp?${buildQueryString(params)}`)
    }
    return(`${extractDomain()}/edge/recruiter-outreach-email/campaing/creation?${buildQueryString(params)}`)
  }


  return (
    <TableCell
      sx={{
        paddingTop: "2.4rem",
        paddingBottom: "1.6rem",
        width: `${TABLE_CONSTANTS.CAMPAIGN_NAME + 8}px`,
        minWidth: `${TABLE_CONSTANTS.CAMPAIGN_NAME + 8}px`,
        border: "none",
      }}
      size="small"
    >
      <Stack spacing={0.7} gap={0.8}>
        <Stack
          direction="row"
          spacing={0.35}
          justifyContent="flex-start"
          alignItems="center"
          gap={0.7}
          flexWrap="wrap"
        >
          {isWhatsAppCampaign ? <WhatsAppIcon /> : <EmailIcon />}
          <CustomStyledTooltip
            title={<ToolTipContent campaignName={campaignName} />}
            arrow
            placement="top"
          >
            <StyledLink
              className="primary"
              underline="hover"
              display={"inline-flex"}
              alignItems={"center"}
              href={status?.toLowerCase()?.includes("draft") ? redirectToCampaignCreate() : undefined}
              target="_blank"
              color={
                status?.toLowerCase()?.includes("draft")
                  ? COLOR_MAP.DARK_PURPLE
                  : COLOR_MAP.DARK_PURPLE
              }
              status={status}
            >
              <CustomTitle>{campaignName}</CustomTitle>
            </StyledLink>
          </CustomStyledTooltip>
          {isBlastCampaign && BLAST_ICON}
        </Stack>
        <Stack display={"block"} spacing={0.24}>
          <DisabledText>{campaignStatus} date:</DisabledText>
          <Text>{date}</Text>
        </Stack>

        {!campaignStageLoading && <Box sx={{ display: "flex" }}>
          <Tag
            tagType={status.toLocaleLowerCase()}
            data={`${status}`}
            // pausedBy={pausedBy || marketyReason}
            pausedBy={(reason?.includes("{time}") && resumeAt) ? reason.replace("{time}", `⏱️ ${getRemainingHrs(resumeAt||"")}` || '') : (reason || marketyReason)}
          />
        </Box>}
      </Stack>
    </TableCell>
  );
};

export default CampaignName;
